// src/pages/ia-paca/articles/ai-engineers-rag-production.tsx
//
// Article-canon Sprint #2 IA PACA — "80% des AI Engineers PACA ne savent
// pas faire un RAG en production". Tonalité "vérités inconfortables",
// signature Gérald Gaillard, format long (~2500 mots), JSON-LD Article.

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../../components/layout/layout';
import IaPacaCTA from '../../../components/ia-paca/IaPacaCTA';
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Calendar,
  User,
  Clock,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const PUBLISHED_DATE = '2026-05-22';
const READING_TIME = '12 min';

export default function AiEngineersRagProductionArticle() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '80% des "AI Engineers" PACA ne savent pas faire un RAG en production',
    description:
      "Enquête terrain sur le marché de l'AI Engineer en Provence-Alpes-Côte d'Azur en 2026. Pourquoi le titre 'AI Engineer' est devenu un attrape-CV, comment qualifier les vrais profils, et ce que vous risquez si vous recrutez le mauvais.",
    image: 'https://recruteurs.io/og-default.jpg',
    datePublished: PUBLISHED_DATE,
    dateModified: PUBLISHED_DATE,
    author: {
      '@type': 'Person',
      name: 'Gérald Gaillard',
      url: 'https://www.linkedin.com/in/geraldgaillard/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Recruteurs.io',
      logo: {
        '@type': 'ImageObject',
        url: 'https://recruteurs.io/logo-recruteurs-io.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://recruteurs.io/ia-paca/articles/ai-engineers-rag-production',
    },
    keywords:
      'AI Engineer PACA, recrutement IA Marseille, RAG production, faux AI Engineer, qualifier AI Engineer, AI Engineer Sophia Antipolis',
  };

  return (
    <Layout
      title='80% des "AI Engineers" PACA ne savent pas faire un RAG en production | recruteurs.io'
      description="Enquête terrain : pourquoi le marché AI Engineer en PACA est inondé de faux profils, et comment qualifier les vrais en 5 questions. Par Gérald Gaillard, Recruteurs.io."
      keywords="AI Engineer PACA, recrutement AI Engineer Marseille, RAG production, faux AI Engineer, qualifier AI Engineer, AI Engineer Sophia Antipolis"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Articles', href: '/ia-paca' },
        {
          name: '80% des AI Engineers PACA ne savent pas faire un RAG en production',
          href: '/ia-paca/articles/ai-engineers-rag-production',
        },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero article */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-violet-50/40 py-12 lg:py-16">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 -right-20 h-80 w-80 rounded-full bg-pink-300/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Retour au hub IA PACA
          </Link>

          <div className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-rose-700">
            <AlertTriangle className="h-3 w-3" />
            Vérité inconfortable · IA PACA
          </div>

          <h1 className="mt-5 text-3xl font-bold leading-[1.15] tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            80% des "AI Engineers" PACA ne savent pas{' '}
            <span className="bg-gradient-to-r from-pink-600 via-orange-500 to-violet-600 bg-clip-text text-transparent">
              faire un RAG en production
            </span>
            .
          </h1>

          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Enquête terrain sur 6 mois d'entretiens, plus de 80 candidats
            qualifiés "AI Engineer" sur Marseille, Sophia, Aix et Nice. Le
            constat est brutal — et c'est un problème pour vos recrutements.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              <a
                href="https://www.linkedin.com/in/geraldgaillard/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-slate-700 hover:underline"
              >
                Gérald Gaillard
              </a>
              <span>· Fondateur Recruteurs.io</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              22 mai 2026
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {READING_TIME} de lecture
            </span>
          </div>
        </div>
      </section>

      {/* Corps article */}
      <article className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-6">
          <div className="prose prose-slate prose-lg max-w-none">
            <h2 id="constat">Le constat — sur 80+ candidats qualifiés, 16 savent faire</h2>

            <p>
              Sur les 6 derniers mois, dans le cadre de briefs clients sur
              des postes "AI Engineer" en Provence-Alpes-Côte d'Azur, nous
              avons rencontré ou qualifié plus de 80 profils qui se
              présentent comme AI Engineer sur LinkedIn et leurs CVs.
            </p>

            <p>
              À une question simple — <em>"Décris-moi un RAG que tu as mis
              en production. Comment tu l'évaluais ?"</em> — environ{' '}
              <strong>16 candidats sur 80 ont donné une réponse qui tenait
              debout</strong>. Soit 20%. Soit, en miroir : 80% des "AI
              Engineers" du marché PACA ne savent pas faire un RAG en
              production.
            </p>

            <p>
              Ce chiffre n'est pas exact à 1% près. C'est un ordre de
              grandeur. Mais l'écart entre la promesse du titre et la
              réalité est tel qu'il mérite un article.
            </p>

            <div className="not-prose my-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                <div>
                  <h3 className="text-base font-bold text-amber-900">
                    Pourquoi cet article ?
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-amber-800">
                    Pour les dirigeants qui s'apprêtent à recruter leur
                    premier AI Engineer en PACA et qui pensent qu'il suffit
                    de poster une annonce avec "LangChain / RAG" pour faire
                    venir des bons profils. Spoiler : non.
                  </p>
                </div>
              </div>
            </div>

            <h2 id="cause">Pourquoi le marché est saturé de faux profils</h2>

            <p>
              Le titre "AI Engineer" est devenu en 18 mois ce que "Data
              Scientist" était devenu en 2018 : un titre fourre-tout, sexy
              à mettre sur LinkedIn, qui ne garantit aucune compétence
              réelle.
            </p>

            <p>Trois forces ont convergé pour créer cette inflation :</p>

            <h3>1. La vague des bootcamps et certificats de 8 semaines</h3>

            <p>
              Depuis l'explosion ChatGPT fin 2022, des dizaines de bootcamps
              "Become an AI Engineer in 8 weeks" ont saturé LinkedIn. Sur le
              fond, beaucoup donnent une base sympa (LangChain, un Pinecone,
              quelques prompts). Sur le format : 8 semaines ne suffisent à
              personne pour acquérir la maturité production. Pourtant ces
              candidats ressortent avec "AI Engineer" sur leur LinkedIn.
            </p>

            <h3>2. Le re-badging par opportunisme</h3>

            <p>
              Le second flux vient de profils existants — Data Scientists,
              Data Analysts, développeurs full-stack — qui ont fait un POC
              ChatGPT en interne et qui repositionnent leur LinkedIn en{' '}
              <em>"AI Engineer | LLM | RAG | Agents | Prompt Engineering"</em>.
            </p>

            <p>
              Ce n'est pas malhonnête en soi, beaucoup ont effectivement
              touché à des LLM. Mais entre "j'ai branché une API OpenAI sur
              un Streamlit en hackathon" et "j'ai mis un RAG fiable en prod
              avec évaluation, monitoring et cache sémantique", il y a deux
              ans d'expérience.
            </p>

            <h3>3. Les recruteurs qui ne savent pas qualifier</h3>

            <p>
              Le 3e moteur, le plus gênant : les cabinets RH qui font le
              relais sans pouvoir filtrer. Quand le recruteur n'a jamais vu
              passer un seul RAG en production, il ne peut pas distinguer le
              candidat qui dit "j'ai utilisé LangChain" du candidat qui dit
              "j'ai utilisé LangChain mais on l'a viré pour aller en
              LlamaIndex parce que notre chunking custom devenait
              intenable".
            </p>

            <p>
              Le résultat : 8 candidats sur 10 transmis à votre DSI sont des
              faux positifs. Vous perdez 3 mois d'entretiens, vous laissez
              passer les vrais profils qui pendant ce temps signent
              ailleurs, et vous finissez frustré.
            </p>

            <h2 id="rag">Qu'est-ce qu'un "vrai RAG en production" ?</h2>

            <p>
              Un RAG — Retrieval-Augmented Generation — c'est l'architecture
              dominante des produits IA d'entreprise depuis 2023 : on
              indexe des documents internes dans une base vectorielle, on
              récupère les passages pertinents pour une question, et on les
              passe à un LLM en contexte pour qu'il génère une réponse
              ancrée dans vos données.
            </p>

            <p>
              Sur le papier, en 50 lignes de Python, on peut faire tourner
              un RAG. Sur Twitter, on en voit passer un par heure. En
              production réelle, dans une entreprise qui doit gérer des
              utilisateurs, des coûts, des SLA, des hallucinations, du
              versioning de prompts et des cas limites, c'est un système
              complexe qui demande au minimum :
            </p>

            <ul>
              <li>
                <strong>Pipeline d'ingestion</strong> : chunking adapté au
                type de document (un chunking naïf casse 80% de la
                qualité), extraction métadonnées, déduplication, gestion
                des mises à jour incrémentales.
              </li>
              <li>
                <strong>Stratégie de retrieval</strong> : hybride dense +
                sparse (BM25 + embeddings), reranker (Cohere, voyage,
                cross-encoders), filtres métadonnées, query
                transformation.
              </li>
              <li>
                <strong>Évaluation</strong> : un dataset d'évaluation
                construit avec les experts métier, des métriques (recall@k,
                MRR, faithfulness, answer relevance), un harness type{' '}
                <code>ragas</code>, <code>deepeval</code>, ou
                équivalent maison.
              </li>
              <li>
                <strong>Monitoring et observability</strong> : Langfuse,
                Phoenix, Helicone — tracking des prompts, latences,
                coûts par requête, suivi des hallucinations remontées par
                les users.
              </li>
              <li>
                <strong>Sécurité & guardrails</strong> : protection contre
                les prompt injections, filtrage PII, contrôle d'accès aux
                documents indexés (un RAG sans ACL est une fuite de données
                qui s'ignore).
              </li>
              <li>
                <strong>Coûts</strong> : caching sémantique, batching,
                routing intelligent entre modèles (Haiku/Sonnet, Mistral
                Small/Large, etc.), budget par utilisateur ou par tenant.
              </li>
            </ul>

            <p>
              Maîtriser ces 6 dimensions, c'est ce qui sépare un AI Engineer
              qui peut vous livrer un produit IA qui dure d'un AI Engineer
              qui vous livre un POC qui plante au 1er client.
            </p>

            <h2 id="signaux">Les 5 questions qui démasquent en 15 minutes</h2>

            <p>
              Voici les 5 questions qu'on pose systématiquement en
              pré-qualification d'un AI Engineer. Aucune ne demande de
              coder. Toutes révèlent en quelques minutes si le candidat a
              vraiment shippé un RAG, ou s'il a vu passer un tuto.
            </p>

            <div className="not-prose my-8 space-y-4">
              {QUESTIONS.map((q, i) => (
                <div
                  key={q.q}
                  className="rounded-2xl border border-slate-200 bg-white p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-slate-900">
                        {q.q}
                      </h4>
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Vrai AI Engineer
                          </div>
                          <p className="mt-1 text-xs leading-relaxed text-emerald-900">
                            {q.good}
                          </p>
                        </div>
                        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-700">
                            <XCircle className="h-3.5 w-3.5" />
                            Faux AI Engineer
                          </div>
                          <p className="mt-1 text-xs leading-relaxed text-rose-900">
                            {q.bad}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 id="couts">Ce que coûte un mauvais recrutement AI Engineer</h2>

            <p>
              Un AI Engineer mal recruté en PACA, c'est en moyenne :
            </p>

            <ul>
              <li>
                <strong>3 à 6 mois de salaire chargé</strong> (40-70 k€) sur
                un profil qui ne livrera pas le produit annoncé.
              </li>
              <li>
                <strong>2 à 4 mois de bandwidth dirigeant</strong> à
                débugger, recadrer, parfois licencier — temps qui aurait
                dû aller sur la stratégie produit.
              </li>
              <li>
                <strong>6 à 12 mois de retard</strong> sur la roadmap IA,
                parce que le POC livré ne passe pas en prod et qu'il faut
                tout reprendre avec un autre profil.
              </li>
              <li>
                <strong>Coût d'opportunité</strong> : les vrais AI Engineers
                que vous avez croisés au début ont signé ailleurs, et il
                faut maintenant chasser ceux qui restent — souvent plus
                chers, parce qu'ils savent que vous êtes désespéré.
              </li>
            </ul>

            <p>
              Total : un mauvais recrutement AI Engineer coûte facilement
              <strong> 80 à 150 k€</strong> à votre boîte, sans compter le
              moral des équipes. À ce prix, prendre 3 semaines de plus pour
              qualifier 5 profils est de loin la meilleure décision.
            </p>

            <h2 id="paca">Le marché PACA en particulier</h2>

            <p>
              Trois éléments rendent le marché PACA spécifique pour les
              AI Engineers :
            </p>

            <p>
              <strong>D'abord, l'offre est limitée mais en croissance.</strong>{' '}
              Nous estimons entre 200 et 350 vrais AI Engineers (au sens
              "shippé un produit LLM en prod sur au moins 2 ans") sur la
              région entre Marseille, Aix, Sophia et Nice. C'est peu. Et la
              moitié est en CDI dans des corporates qui paient bien
              (Amadeus, Mistral via partenariats, scale-ups locales).
            </p>

            <p>
              <strong>Ensuite, la concurrence parisienne en remote est
              brutale.</strong> Les boîtes parisiennes proposent des
              packages 80-130 k€ en télétravail intégral. Si vous proposez
              60 k€ en présentiel à Marseille, vous ne capterez que les
              candidats qui ont une raison personnelle de rester (famille,
              cadre de vie). Ce n'est pas mauvais en soi, mais il faut
              calibrer ses attentes.
            </p>

            <p>
              <strong>Enfin, l'écosystème local pousse l'offre.</strong>{' '}
              Le partenariat CMA CGM × Mistral AI annoncé fin 2024 (100 M€,
              hub IA basé à Marseille), l'inauguration du Pôle Alpha à
              Sophia en janvier 2026, la dynamique 3IA Côte d'Azur et la
              House of AI : la demande explose, l'offre suit avec un
              décalage. C'est exactement la situation dans laquelle on
              recrute mal si on ne sait pas qualifier.
            </p>

            <h2 id="conclusion">Conclusion — ce qu'on recommande</h2>

            <p>
              Si vous recrutez votre premier AI Engineer en PACA en 2026 :
            </p>

            <ol>
              <li>
                <strong>Ne vous fiez pas au titre LinkedIn.</strong> Le
                titre "AI Engineer" couvre aujourd'hui des écarts de
                compétence de 1 à 10.
              </li>
              <li>
                <strong>Posez les 5 questions ci-dessus.</strong> Elles
                font le tri en 15 minutes. Si vos questions actuelles
                ressemblent à "tu connais LangChain ?", vous êtes par
                définition incapable de qualifier.
              </li>
              <li>
                <strong>Faites un test technique court mais réaliste.</strong>{' '}
                Pas un leetcode. Un mini-RAG sur un PDF métier + un
                paragraphe d'évaluation. 4h maximum. Ça révèle plus que 10
                entretiens.
              </li>
              <li>
                <strong>Calibrez le package au marché réel.</strong> 45-55
                k€ junior, 55-75 k€ confirmé, 75-105 k€ senior, 100-140 k€
                expert. Plus bas, vous ne captez que les faux positifs.
              </li>
              <li>
                <strong>Préparez votre offre.</strong> Stack tech à jour
                (pas du Python 3.6 ni du Tensorflow 1), accès GPU ou crédits
                cloud, autonomie sur les choix de modèles, vision produit
                claire. Les vrais AI Engineers en PACA ont l'embarras du
                choix, ils choisissent le projet en premier, le salaire en
                second.
              </li>
            </ol>

            <p>
              Et si vous voulez déléguer cette qualification à un cabinet
              qui maîtrise le sujet — c'est exactement ce qu'on fait. Notre
              process commence par un brief stratégique de 30 minutes pour
              comprendre votre vrai besoin, et finit par une court-list de
              5 profils qui passent nos 5 questions.
            </p>

            <p className="text-sm italic text-slate-500">
              Gérald Gaillard, fondateur de Recruteurs.io. Cabinet de
              recrutement IA spécialisé PACA. Vous pouvez me joindre via{' '}
              <a
                href="https://www.linkedin.com/in/geraldgaillard/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-pink-700 hover:underline"
              >
                LinkedIn
              </a>{' '}
              ou réserver un créneau directement sur la page{' '}
              <Link to="/reservation" className="font-semibold text-pink-700 hover:underline">
                Réservation
              </Link>
              .
            </p>
          </div>
        </div>
      </article>

      {/* Liens internes */}
      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-3xl px-6">
          <h3 className="text-base font-bold uppercase tracking-wider text-slate-500">
            À lire aussi
          </h3>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <Link
              to="/ia-paca/metiers/ai-engineer"
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-pink-300 hover:shadow-md"
            >
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                  Page métier
                </div>
                <div className="mt-1 text-base font-bold text-slate-900">
                  Recruter un AI Engineer en PACA
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-pink-600" />
            </Link>
            <Link
              to="/ia-paca/metiers/ml-engineer"
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-pink-300 hover:shadow-md"
            >
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                  Page métier
                </div>
                <div className="mt-1 text-base font-bold text-slate-900">
                  Recruter un ML Engineer en PACA
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-pink-600" />
            </Link>
            <Link
              to="/ia-paca/marseille"
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-pink-300 hover:shadow-md"
            >
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                  Page géo
                </div>
                <div className="mt-1 text-base font-bold text-slate-900">
                  Recrutement IA à Marseille
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-pink-600" />
            </Link>
            <Link
              to="/ia-paca/sophia-antipolis"
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-pink-300 hover:shadow-md"
            >
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                  Page géo
                </div>
                <div className="mt-1 text-base font-bold text-slate-900">
                  Recrutement IA à Sophia Antipolis
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-pink-600" />
            </Link>
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous voulez recruter un vrai AI Engineer en PACA ?"
        subtitle="Brief 30 minutes pour qualifier votre besoin, court-list de 5 candidats qui passent nos 5 questions. Garantie de remplacement sur 6 mois."
      />
    </Layout>
  );
}

const QUESTIONS = [
  {
    q: "Décris-moi le dernier RAG que tu as mis en production. Comment tu l'évaluais ?",
    good:
      "Cite un cas concret : type de documents, choix de chunking, modèle d'embedding, base vectorielle, reranker. Décrit le dataset d'évaluation construit avec les experts métier, les métriques (faithfulness, answer relevance, recall@k), le harness (ragas, deepeval, ou maison). Sait raconter ce qui ne marchait pas et comment il l'a corrigé.",
    bad: "'On a fait du LangChain avec Pinecone, ça marchait bien.' Pas de métrique, pas de dataset d'éval, pas de notion de faithfulness. A déployé un POC qu'il appelle prod.",
  },
  {
    q: "Quelle est la pire hallucination que tu as eue en production, et qu'est-ce que tu as fait après ?",
    good:
      "Raconte un incident précis : tel utilisateur a reçu telle info inventée parce que tel passage du RAG était hors contexte / trop court / mal chunké. Décrit ce qu'il a mis en place après : meilleurs filtres metadata, reranker, prompt avec contraintes 'cite tes sources', alertes Langfuse, peut-être un refus de répondre si la confiance est faible.",
    bad: "'On n'a pas vraiment eu d'hallucination, le modèle est bien.' Réponse impossible si on tourne en prod plus de 6 mois. Signifie soit qu'il n'a pas tourné en prod, soit qu'il n'a aucun monitoring.",
  },
  {
    q: "Combien coûte ton RAG par mois, et comment tu as réduit ce coût ?",
    good:
      "Donne un ordre de grandeur (X € / 1 000 requêtes), explique le routing entre modèles (Haiku/Sonnet, Mistral Small/Large), le cache sémantique (vector cache ou Redis hash), le batching, la troncature de contexte. Sait quel poste pèse le plus (embeddings vs génération vs reranker).",
    bad: "'On utilise GPT-4o pour tout, c'est fiable.' Pas de notion coût/perf, pas de routing, pas de cache, brûle du budget cloud sans mesurer.",
  },
  {
    q: "Comment tu gères la mise à jour de tes documents indexés ?",
    good:
      "Décrit le pipeline d'ingestion incrémentale (CDC, batch nocturne, webhook), gestion des suppressions et des updates (overwrite vs append vs versioning), invalidation cache, recalcul des embeddings sur changement de modèle. Pense aux ACL : un doc retiré du système source doit être retiré du RAG.",
    bad: "'On réindexe tout chaque semaine.' Cher, lent, casse les performances. Ne pense pas aux ACL — un RAG sans contrôle d'accès est un risque RGPD direct.",
  },
  {
    q: "Tu dois choisir entre LangChain, LlamaIndex, Haystack ou tout en maison. Tu choisis quoi pour un client B2B avec 1 M documents et SLA strict ?",
    good:
      "Réponse nuancée : ça dépend de la maturité de l'équipe, des contraintes (multi-tenant, traçabilité), du scope (RAG simple vs agents complexes). Parle d'éviter le lock-in framework, de garder la couche retrieval découplée du framework agent, de la possibilité d'utiliser plusieurs briques. Cite ses retours d'expérience.",
    bad: "'LangChain c'est le standard.' Ou 'On fait tout en maison parce que c'est mieux.' Réponse dogmatique. Pas de réflexion sur le contexte, la dette technique, l'équipe.",
  },
];
